import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Chip } from '@mui/material';
import { Pause as PauseIcon } from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { useSocket } from '../contexts/SocketContext';
import { useSyncedYouTubePlayer } from '../hooks/useSyncedYouTubePlayer';
import PlayerControlPanel from '../components/PlayerControlPanel';
import GoldenBuzzerSplash from '../components/GoldenBuzzerSplash';
import { GONG_IMAGE_URL, GONG_AUDIO_URL, getActiveGongEntries } from '../constants/gongs';
import { formatTimestamp } from '../utils/format';

// High-tech reveal animation
const techReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.85) rotateX(12deg);
    filter: blur(20px) brightness(0.4);
  }
  40% {
    opacity: 0.7;
    filter: blur(8px) brightness(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotateX(0deg);
    filter: blur(0) brightness(1);
  }
`;

// High-tech hide animation (removed; not used)

// Glowing border pulse
const borderPulse = keyframes`
  0%, 100% {
    box-shadow: 
      0 0 20px rgba(0, 200, 255, 0.3),
      0 0 40px rgba(0, 200, 255, 0.2),
      inset 0 0 20px rgba(0, 200, 255, 0.1);
  }
  50% {
    box-shadow: 
      0 0 30px rgba(0, 200, 255, 0.5),
      0 0 60px rgba(0, 200, 255, 0.3),
      inset 0 0 30px rgba(0, 200, 255, 0.2);
  }
`;

// Corner accents animation
const cornerGlow = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const gongStrike = keyframes`
  0% {
    transform: scale(0.6) rotate(-6deg);
    opacity: 0;
    filter: drop-shadow(0 0 0 rgba(252, 186, 3, 0));
  }
  45% {
    transform: scale(1.08) rotate(6deg);
    opacity: 1;
    filter: drop-shadow(0 6px 12px rgba(252, 186, 3, 0.45));
  }
  65% {
    transform: scale(0.95) rotate(-3deg);
  }
  85% {
    transform: scale(1.02) rotate(1deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    filter: drop-shadow(0 6px 18px rgba(252, 186, 3, 0.35));
  }
`;

const gongVibrate = keyframes`
  0%, 100% {
    transform: translate3d(0, 0, 0) rotate(0deg);
  }
  20% {
    transform: translate3d(-2px, 1px, 0) rotate(-1deg);
  }
  40% {
    transform: translate3d(2px, -1px, 0) rotate(1deg);
  }
  60% {
    transform: translate3d(-1px, 0, 0) rotate(-0.5deg);
  }
  80% {
    transform: translate3d(1px, 0, 0) rotate(0.5deg);
  }
`;

const OverlayContainer = ({ children }) => (
  <Box
    sx={{
      width: '100vw',
      height: '100vh',
      bgcolor: 'transparent',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}
  >
    {children}
  </Box>
);

const Metadata = ({ title, submitter, sx }) => {
  if (!title) return null;

  return (
    <Box
      sx={{
        minWidth: '40vw',
        maxWidth: '70vw',
        bgcolor: 'rgba(0,0,0,0.7)',
        borderRadius: 2,
        px: 3,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...sx
      }}
    >
      <Typography variant="h5" fontWeight={700} noWrap>
        {title}
      </Typography>
      {submitter && (
        <Chip
          size="small"
          label={`Submitted by ${submitter}`}
          sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}
        />
      )}
    </Box>
  );
};


const getSubmitterUsername = (item) =>
  item?.submitter?.twitchUsername || item?.submitterUsername || null;

const PlayerOverlay = () => {
  const { channelName } = useParams();
  const [searchParams] = useSearchParams();
  const hideQueue = searchParams.get('queue') === 'hide';
  const showControls = searchParams.get('controls') === 'show' && !hideQueue;
  // The OBS link carries a token that lets this page report playback to the
  // room (the dashboard's Player Overlay link includes it). Without one the
  // page still plays along; it just cannot report.
  const overlayToken = (searchParams.get('token') || '').trim();

  const {
    connectToChannel,
    disconnectFromChannel,
    queue,
    currentlyPlaying,
    playNext,
    skipCurrent,
    playOverlay,
    pauseOverlay,
    seekOverlay,
    addChannelListener,
    removeChannelListener,
    emitToChannel,
    channelConnected,
    votingState,
    startVotingSession,
    overlayShowPlayer,
    gongState,
    goldenBuzzerEvent
  } = useSocket();

  const [pendingSeek, setPendingSeek] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [shouldShowPlayer, setShouldShowPlayer] = useState(false);

  const currentVideoId = currentlyPlaying?.id ?? null;
  const activeGongs = useMemo(
    () => getActiveGongEntries(gongState, currentVideoId),
    [gongState, currentVideoId]
  );
  const gongSeenRef = useRef(new Set());
  const gongAudioRef = useRef(null);
  const playGongAudio = useCallback(() => {
    if (!GONG_AUDIO_URL) {
      return;
    }
    try {
      // Clean up previous Audio instance before creating a new one
      if (gongAudioRef.current) {
        gongAudioRef.current.pause();
        gongAudioRef.current.src = '';
      }
      const audio = new Audio(GONG_AUDIO_URL);
      audio.volume = 1;
      gongAudioRef.current = audio;
      audio.play().catch(() => {});
    } catch (err) {
      console.warn('Failed to play gong audio on overlay', err);
    }
  }, []);

  useEffect(() => {
    gongSeenRef.current = new Set();
  }, [currentVideoId]);

  useEffect(() => {
    const previous = gongSeenRef.current || new Set();
    const nextIds = new Set();
    activeGongs.forEach((entry) => {
      nextIds.add(entry.id);
      if (!previous.has(entry.id)) {
        playGongAudio();
      }
    });
    gongSeenRef.current = nextIds;
  }, [activeGongs, playGongAudio]);

  // Clean up gong Audio instance on unmount
  useEffect(() => {
    return () => {
      if (gongAudioRef.current) {
        gongAudioRef.current.pause();
        gongAudioRef.current.src = '';
        gongAudioRef.current = null;
      }
    };
  }, []);

  // Check if voting is active for the CURRENT video that's playing
  const votingActive = useMemo(() => {
    if (!votingState || !votingState.stage || votingState.stage === 'cancelled') {
      return false;
    }
    // Only consider voting active if it's for the current video
    return votingState.queueItemId === currentVideoId;
  }, [votingState, currentVideoId]);
  const currentCupId = useMemo(
    () => currentlyPlaying?.cupId || votingState?.cupId || null,
    [currentlyPlaying?.cupId, votingState?.cupId]
  );

  const handleVote = useCallback(async () => {
    if (!currentVideoId || !currentCupId || votingActive) {
      return;
    }
    try {
      await startVotingSession(currentVideoId, currentCupId);
    } catch (error) {
      console.error('Failed to start voting session from overlay:', error);
    }
  }, [currentVideoId, currentCupId, startVotingSession, votingActive]);

  const skipDisabled = !currentVideoId;
  const playNextDisabled = !Array.isArray(queue) || queue.length === 0;
  const voteDisabled = !currentVideoId || !currentCupId || votingActive;

  useEffect(() => {
    const previousBodyBg = document.body.style.backgroundColor;
    const previousBodyImage = document.body.style.backgroundImage;
    const previousHtmlBg = document.documentElement.style.backgroundColor;
    const rootElement = document.getElementById('root');
    const previousRootBg = rootElement ? rootElement.style.backgroundColor : null;

    document.body.style.backgroundColor = 'rgba(0, 0, 0, 0)';
    document.body.style.backgroundImage = 'none';
    document.documentElement.style.backgroundColor = 'rgba(0, 0, 0, 0)';
    if (rootElement) {
      rootElement.style.backgroundColor = 'rgba(0, 0, 0, 0)';
    }

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
      document.body.style.backgroundImage = previousBodyImage;
      document.documentElement.style.backgroundColor = previousHtmlBg;
      if (rootElement && previousRootBg !== null) {
        rootElement.style.backgroundColor = previousRootBg;
      }
    };
  }, []);

  useEffect(() => {
    if (!channelName) return;
    const opts = { explicit: true };
    if (overlayToken) {
      opts.auth = { overlayToken };
    }
    connectToChannel(channelName, opts);
    return () => disconnectFromChannel();
  }, [channelName, overlayToken, connectToChannel, disconnectFromChannel]);

  useEffect(() => {
    setPendingSeek(null);
    setIsSeeking(false);
  }, [currentlyPlaying?.id]);

  // Track video changes and show/hide player
  // Hide player when voting is active; otherwise honor manual override if present
  useEffect(() => {
    const vidId = currentlyPlaying?.videoId;
    const manual = (overlayShowPlayer === true || overlayShowPlayer === false) ? overlayShowPlayer : null;
    const finalShow = !votingActive && (manual !== null ? manual : Boolean(vidId));

    setShouldShowPlayer(finalShow);
  }, [currentlyPlaying?.videoId, votingActive, overlayShowPlayer]);

  const {
    containerRef,
    playLocal,
    pauseLocal,
    seekLocal,
    setVolume: setPlayerVolume,
    toggleMute,
    currentTime,
    duration,
    volume,
    muted,
    hasVideo,
    isPaused,
    playerReady,
    requestServerState
  } = useSyncedYouTubePlayer({
    videoId: currentlyPlaying?.videoId,
    startSeconds: currentlyPlaying?.startTime || 0,
    endSeconds: currentlyPlaying?.endTime || 0,
    channelConnected,
    addChannelListener,
    removeChannelListener,
    emitToChannel,
    initialVolume: 100,
    defaultMuted: false,
    autoPlayOnReady: false,
    // The stream view IS the room's player: it reports what its player does
    // so everyone else follows it, which the server only accepts with the
    // overlay token. The controls view is a remote control and only sends
    // deliberate actions.
    reportPlayerState: !showControls && Boolean(overlayToken),
    onLocalPlay: playOverlay,
    onLocalPause: pauseOverlay,
    onLocalSeek: seekOverlay
  });

  // The controls view falls in with the room once its player exists, instead
  // of sitting cued at the start until someone clicks.
  useEffect(() => {
    if (!showControls || !playerReady || !channelConnected || !currentlyPlaying?.videoId) {
      return;
    }
    requestServerState();
  }, [showControls, playerReady, channelConnected, currentlyPlaying?.videoId, requestServerState]);

  const resolvedDuration = typeof duration === 'number' && duration > 0 ? duration : 0;
  const sliderDisabled = !channelConnected || !hasVideo || resolvedDuration === 0;
  const displayTime = isSeeking && typeof pendingSeek === 'number'
    ? pendingSeek
    : (typeof currentTime === 'number' ? currentTime : 0);
  const sliderValue = resolvedDuration > 0
    ? Math.min(Math.max(displayTime, 0), resolvedDuration)
    : 0;

  const handleSeekChange = (_, value) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (typeof next !== 'number' || Number.isNaN(next)) {
      return;
    }
    setIsSeeking(true);
    setPendingSeek(Math.max(0, next));
  };

  const handleSeekCommit = (_, value) => {
    const next = Array.isArray(value) ? value[0] : value;
    setIsSeeking(false);
    setPendingSeek(null);

    if (sliderDisabled || typeof next !== 'number' || Number.isNaN(next)) {
      return;
    }

    const targetTime = Math.min(Math.max(next, 0), resolvedDuration);
    seekLocal(targetTime);
  };

  const volumeSliderDisabled = !channelConnected || !hasVideo;
  const normalizedVolume = typeof volume === 'number' ? volume : 0;
  const volumeSliderValue = muted ? 0 : normalizedVolume;
  const volumeLabel = muted ? 'Muted' : `${Math.round(normalizedVolume)}%`;

  const handleVolumeChange = (_, value) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (typeof next !== 'number' || Number.isNaN(next)) {
      return;
    }
    setPlayerVolume(next);
  };

  const handleVolumeToggle = () => {
    toggleMute();
  };

  // Bare commands: the room plays or pauses where it is. This copy's own
  // position is not the stream's, so it is never sent along.
  const handlePlay = useCallback(() => {
    if (!channelConnected || !hasVideo) {
      return;
    }
    playLocal();
  }, [channelConnected, hasVideo, playLocal]);

  const handlePause = useCallback(() => {
    if (!channelConnected || !hasVideo) {
      return;
    }
    pauseLocal();
  }, [channelConnected, hasVideo, pauseLocal]);

  const playDisabled = !channelConnected || !hasVideo;
  const pauseDisabled = playDisabled;

  const outerStyles = showControls
    ? {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 3, sm: 4 },
        px: { xs: '5vw', sm: '6vw' },
        py: { xs: '4vh', sm: '6vh' },
        position: 'relative'
      }
    : {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center'
      };

  const videoWrapperStyles = showControls
    ? {
        width: { xs: '100%', sm: '72vw', md: '68vw', lg: '960px' },
        maxWidth: '1280px',
        borderRadius: 1,
        border: hasVideo ? 'none' : '4px solid rgba(255,255,255,0.1)',
        aspectRatio: '16 / 9'
      }
    : {
        width: '100%',
        height: '100%',
        borderRadius: 0,
        border: 'none'
      };

  return (
    <OverlayContainer>
      <Box sx={outerStyles}>
        {/* Player container with high-tech reveal/hide animation */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            display: shouldShowPlayer ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'transparent',
            ...videoWrapperStyles,
            // High-tech border effect when video is playing
            ...(shouldShowPlayer && !showControls ? {
              border: '2px solid rgba(0, 200, 255, 0.4)',
              borderRadius: 2,
              animation: `${techReveal} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${borderPulse} 3s ease-in-out infinite`,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                background: 'linear-gradient(45deg, rgba(0, 200, 255, 0.3), rgba(0, 150, 255, 0.1), rgba(0, 200, 255, 0.3))',
                borderRadius: 2,
                zIndex: -1,
                filter: 'blur(8px)'
              }
            } : {}),
            // Corner accents
            ...(shouldShowPlayer ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -4,
                background: `
                  linear-gradient(90deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) top left,
                  linear-gradient(0deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) top left,
                  linear-gradient(270deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) top right,
                  linear-gradient(0deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) top right,
                  linear-gradient(90deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) bottom left,
                  linear-gradient(180deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) bottom left,
                  linear-gradient(270deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) bottom right,
                  linear-gradient(180deg, rgba(0, 200, 255, 0.6) 0%, transparent 15%) bottom right
                `,
                backgroundSize: '40px 40px, 40px 40px',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top left, top left, top right, top right, bottom left, bottom left, bottom right, bottom right',
                pointerEvents: 'none',
                zIndex: 10,
                animation: `${cornerGlow} 2s ease-in-out infinite`
              }
            } : {})
          }}
        >
          <Box 
            ref={containerRef} 
            id="overlay-player" 
            sx={{ 
              position: 'absolute', 
              inset: 0,
              zIndex: 1
            }} 
          />
          {/* Our own paused screen. It sits over the iframe so YouTube's
              paused chrome (big play button, title bar, related videos) never
              reaches the stream, and mounting/unmounting it forces a repaint
              of the player area when playback resumes. */}
          {shouldShowPlayer && !showControls && hasVideo && isPaused && (
            <Box
              data-testid="overlay-paused-cover"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                background: 'radial-gradient(ellipse at center, rgb(10, 22, 34) 0%, rgb(3, 8, 14) 100%)',
                pointerEvents: 'none'
              }}
            >
              <PauseIcon
                sx={{
                  fontSize: { xs: 64, sm: 96 },
                  color: 'rgba(0, 200, 255, 0.9)',
                  filter: 'drop-shadow(0 0 18px rgba(0, 200, 255, 0.55))'
                }}
              />
              <Typography
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 4,
                  fontWeight: 700,
                  fontSize: { xs: 18, sm: 26 },
                  color: 'rgba(255, 255, 255, 0.92)'
                }}
              >
                Paused
              </Typography>
            </Box>
          )}
          {shouldShowPlayer && activeGongs.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: { xs: '3%', sm: '4%' },
                right: { xs: '3%', sm: '4%' },
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                zIndex: 4,
                pointerEvents: 'none'
              }}
            >
              {activeGongs.map((entry) => (
                <Box
                  key={`${entry.id}-${entry.createdAt || 'active'}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: 'rgba(0,0,0,0.45)',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    minWidth: { xs: 96, sm: 110 },
                    animation: `${gongStrike} 700ms ease-out, ${gongVibrate} 1600ms ease-in-out 700ms infinite`,
                    transformOrigin: 'center'
                  }}
                >
                  <Box
                    component="img"
                    src={GONG_IMAGE_URL}
                    alt="Gong"
                    sx={{
                      width: { xs: 56, sm: 70 },
                      height: { xs: 56, sm: 70 },
                      objectFit: 'cover',
                      borderRadius: 1,
                      boxShadow: '0 0 16px rgba(0,0,0,0.5)'
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      letterSpacing: 1,
                      mt: 0.75,
                      textAlign: 'center'
                    }}
                  >
                    {entry.displayName || 'Judge'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {showControls && !hideQueue && shouldShowPlayer && (
            <Metadata
              title={currentlyPlaying?.title}
              submitter={getSubmitterUsername(currentlyPlaying)}
              sx={{
                position: 'absolute',
                left: '50%',
                bottom: { xs: '4%', sm: '3%' },
                transform: 'translateX(-50%)',
                minWidth: { xs: '60vw', sm: '48vw' },
                maxWidth: { xs: '85vw', sm: '60vw' },
                zIndex: 2
              }}
            />
          )}
        </Box>

        {showControls && (
          <Box sx={{ width: { xs: '100%', sm: '72vw', md: '68vw', lg: '960px' }, maxWidth: '1280px' }}>
            <PlayerControlPanel
              variant="overlay"
              statusChips={[
                {
                  label: channelConnected ? 'Live' : 'Connecting…',
                  color: channelConnected ? 'success' : 'warning'
                },
                {
                  label: hasVideo ? 'On Air' : 'Idle',
                  color: hasVideo ? 'info' : 'default'
                }
              ]}
              headerLabel="Playback Actions"
              currentTimeLabel={formatTimestamp(Math.max(displayTime, 0))}
              durationLabel={formatTimestamp(resolvedDuration)}
              sliderValue={sliderValue}
              sliderMax={resolvedDuration || 1}
              onSeekChange={handleSeekChange}
              onSeekCommit={handleSeekCommit}
              seekDisabled={sliderDisabled}
              onPlay={handlePlay}
              onPause={handlePause}
              onSkip={skipCurrent}
              onVote={handleVote}
              onPlayNext={playNext}
              playDisabled={playDisabled}
              pauseDisabled={pauseDisabled}
              skipDisabled={skipDisabled}
              voteDisabled={voteDisabled}
              playNextDisabled={playNextDisabled}
              volumeValue={volumeSliderValue}
              volumeLabel={volumeLabel}
              muted={muted}
              onVolumeChange={handleVolumeChange}
              onVolumeToggle={handleVolumeToggle}
              volumeDisabled={volumeSliderDisabled}
            />
          </Box>
        )}
      </Box>

      <GoldenBuzzerSplash event={goldenBuzzerEvent} />
    </OverlayContainer>
  );
};

export default PlayerOverlay;
